// VendAgain — my saved ISO alerts.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Compass, Plus, MapPin, DollarSign, Bell, Pause, Play, Trash2, Edit3 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CategoryChip, formatPrice } from "./shared";

export default function VendAgainIsoMine() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendagain/iso/mine");
      setItems(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (iso) => {
    setBusy(iso.id);
    try {
      const { data } = await api.patch(`/vendagain/iso/${iso.id}`, {
        is_active: !iso.is_active,
      });
      setItems((prev) => prev.map((i) => (i.id === iso.id ? data : i)));
      toast.success(data.is_active ? "Alert re-enabled." : "Alert paused.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    } finally { setBusy(null); }
  };

  const remove = async (iso) => {
    if (!window.confirm("Delete this alert?")) return;
    try {
      await api.delete(`/vendagain/iso/${iso.id}`);
      setItems((prev) => prev.filter((i) => i.id !== iso.id));
      toast.success("Alert deleted.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-14" data-testid="iso-mine-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline" style={{ color: "var(--moss)" }}>VendAgain · In Search Of</div>
          <h1 className="font-serif text-4xl">My alerts</h1>
          <p className="mt-1 opacity-70 text-sm">
            Saved "looking for" searches — we&rsquo;ll ping you the moment a match drops.
          </p>
        </div>
        <Link to="/vendagain/iso/new">
          <Button className="btn-primary rounded-full inline-flex items-center gap-2" data-testid="iso-mine-new-btn">
            <Plus size={16} /> New alert
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="opacity-60">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-24 text-center border-2 border-dashed rounded-sm"
              style={{ borderColor: "rgba(0,0,0,0.08)" }}
              data-testid="iso-mine-empty">
          <Compass className="mx-auto h-10 w-10 opacity-30" />
          <h3 className="mt-4 font-serif text-2xl">You haven&rsquo;t saved any alerts yet.</h3>
          <p className="mt-2 text-sm opacity-70">Tell us what you&rsquo;re hunting and we&rsquo;ll do the watching.</p>
          <Link to="/vendagain/iso/new" className="mt-4 inline-block">
            <Button className="btn-primary rounded-full">Save your first alert</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((iso) => (
            <div key={iso.id}
                  className="card-warm rounded-sm p-5"
                  data-testid={`iso-mine-card-${iso.id}`}
                  style={{ opacity: iso.is_active ? 1 : 0.6 }}>
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <CategoryChip value={iso.category} />
                  {!iso.is_active && (
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full"
                          style={{ background: "rgba(0,0,0,0.08)", color: "var(--ink-soft)" }}>
                      Paused
                    </span>
                  )}
                  {iso.match_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full"
                          style={{ background: "rgba(107,47,186,0.14)", color: "var(--avt-purple)" }}>
                      <Bell size={9} /> {iso.match_count} match{iso.match_count === 1 ? "" : "es"}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => toggleActive(iso)}
                    disabled={busy === iso.id}
                    data-testid={`iso-toggle-${iso.id}`}
                    className="text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100"
                  >
                    {iso.is_active ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Resume</>}
                  </button>
                  <Link to={`/vendagain/iso/${iso.id}/edit`}
                          data-testid={`iso-edit-${iso.id}`}
                          className="text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100">
                    <Edit3 size={12} /> Edit
                  </Link>
                  <button type="button"
                            onClick={() => remove(iso)}
                            data-testid={`iso-delete-${iso.id}`}
                            className="text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100"
                            style={{ color: "var(--terracotta)" }}>
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
              <h3 className="font-serif text-xl leading-tight">{iso.title}</h3>
              <p className="mt-1 text-sm opacity-75">{iso.description}</p>
              <div className="mt-3 flex items-center gap-4 text-xs opacity-70 flex-wrap">
                <span className="inline-flex items-center gap-1"><MapPin size={11} /> {iso.city}</span>
                {iso.max_price_cents ? (
                  <span className="inline-flex items-center gap-1 font-bold" style={{ color: "var(--avt-purple)" }}>
                    <DollarSign size={11} /> up to {formatPrice(iso.max_price_cents, false)}
                  </span>
                ) : (
                  <span className="opacity-60">No budget cap</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
